/**
 * «Активность → Автоакцепт» (`?view=accept`, право `accept_view`): акцепт времени салонов по сменам — по
 * каждому сотруднику за период: сколько по статусам, сколько пройдёт автоакцепт, сколько окон требует
 * решения HR, сколько уже принято в CRM и кем; полоска дней и раскрытие по сменам. Только просмотр — в CRM
 * экран не пишет. Спека `docs/specs/2026-11-05-activity-autoaccept-design.md`, «Экран 1».
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router";

import { Tile } from "@/components/activity/DaysView";
import { ShiftBar } from "@/components/activity/ShiftBar";
import { Combo } from "@/components/Combo";
import {
  ACCEPTED_BY,
  type AcceptDay,
  type AcceptPerson,
  type AcceptTotals,
  ALL_SALONS_MAX_DAYS,
  loadAcceptPeriod,
  retryServerErrors,
  RULE_LABEL,
  RULES,
  type Rule,
  VERDICT,
  VERDICTS,
  type Verdict,
} from "@/lib/accept";
import { addDays, dm, weekday } from "@/lib/delivery";
import { workTime } from "@/lib/leads";

const ZERO: AcceptTotals = {
  shifts: 0,
  paid_min: 0,
  auto_min: 0,
  auto_pending_min: 0,
  manual_min: 0,
  manual_count: 0,
  accepted_min: 0,
  robot_days: 0,
  hr_days: 0,
};

function sum(people: AcceptPerson[]): AcceptTotals {
  const t = { ...ZERO };
  for (const p of people) for (const k of Object.keys(t) as (keyof AcceptTotals)[]) t[k] += p.totals[k];
  return t;
}

/** Дни периода по порядку, включительно. */
function daysOf(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length < 62; d = addDays(d, 1)) out.push(d);
  return out;
}

/** Понедельник недели дня — неделя = период зарплаты, как `timeList_period` Резерва. */
function monday(day: string): string {
  const shift = (["пн", "вт", "ср", "чт", "пт", "сб", "вс"] as const).indexOf(weekday(day) as "пн");
  return addDays(day, -shift);
}

const shiftHref = (user: number, day: string) => `/activity/accept/${user}?date=${day}`;

function Badge({ v }: { v: Verdict }) {
  return (
    <span className={`inline-block rounded-[var(--radius-ui)] px-1.5 py-0.5 text-[11.5px] leading-tight ${VERDICT[v].tone}`} title={VERDICT[v].hint}>
      {VERDICT[v].label}
    </span>
  );
}

/** Полоска дней: квадрат на каждый день периода цветом вердикта; день без смены — пустая рамка. */
function DayStrip({ days, byDate, onPick }: { days: string[]; byDate: Map<string, AcceptDay>; onPick: (day: string) => void }) {
  return (
    <div className="flex flex-wrap gap-[3px]" data-annot="полоска дней">
      {days.map((d) => {
        const x = byDate.get(d);
        if (!x)
          return <span key={d} className="size-3 rounded-[2px] border border-border" title={`${dm(d)} ${weekday(d)} — смены нет`} />;
        const v = VERDICT[x.verdict];
        return (
          <button
            key={d}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPick(d);
            }}
            className={`size-3 rounded-[2px] ${v.dot} hover:outline hover:outline-1 hover:outline-fg`}
            title={`${dm(d)} ${weekday(d)} · ${v.label} · по статусам ${workTime(x.paid_min)}, автоакцепт ${workTime(x.auto_min)}, окон ${x.manual_count}${x.accepted_min ? `, в CRM ${workTime(x.accepted_min)}` : ""}`}
            aria-label={`${dm(d)}: ${v.label}`}
          />
        );
      })}
    </div>
  );
}

const td = "whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums";
const th = "cursor-help whitespace-nowrap px-2.5 py-2 text-right font-medium underline decoration-dotted underline-offset-2";

/** Смены сотрудника по дням, неделя — разделителем, как `timeList_period` Резерва. */
function Shifts({ p }: { p: AcceptPerson }) {
  const weeks = new Map<string, AcceptDay[]>();
  for (const d of p.days) weeks.set(monday(d.date), [...(weeks.get(monday(d.date)) ?? []), d]);
  return (
    <tr>
      <td colSpan={7} className="bg-bg px-4 pb-3 pt-1">
        <table className="w-full text-[12px]" data-annot={`смены «${p.user.name}»`}>
          <tbody>
            {[...weeks].map(([mon, days]) => {
              const w = days.reduce(
                (t, d) => ({ paid: t.paid + d.paid_min, auto: t.auto + d.auto_min, man: t.man + d.manual_count, acc: t.acc + d.accepted_min }),
                { paid: 0, auto: 0, man: 0, acc: 0 },
              );
              return (
                <Fragment key={mon}>
                  <tr className="border-b border-border text-fg-muted">
                    <td colSpan={8} className="pb-1 pt-2 font-medium">
                      пн {dm(mon)} – вс {dm(addDays(mon, 6))}
                      <span className="ml-2 font-normal text-fg-faint">
                        по статусам {workTime(w.paid)} · автоакцепт {workTime(w.auto)} · окон {w.man} · в CRM {workTime(w.acc)}
                      </span>
                    </td>
                  </tr>
                  {days.map((d) => (
                    <tr key={d.date} className="border-b border-border last:border-b-0" data-annot={`смена ${d.date}`}>
                      <td className="whitespace-nowrap py-1.5 pr-2 tabular-nums">
                        {dm(d.date)} <span className="text-fg-faint">{weekday(d.date)}</span>
                      </td>
                      <td className="py-1.5 pr-2">
                        <Badge v={d.verdict} />
                      </td>
                      <td className={td} title="по статусам">{workTime(d.paid_min)}</td>
                      <td className={td} title="пройдёт автоакцепт">{workTime(d.auto_min)}</td>
                      <td className={td} title="окна «требует решения»">
                        {d.manual_count ? (
                          <span className="text-warn">
                            {d.manual_count} ({workTime(d.manual_min)})
                          </span>
                        ) : (
                          <span className="text-fg-faint">—</span>
                        )}
                      </td>
                      <td className={td} title="принято в CRM">
                        {d.accepted_min ? (
                          <>
                            {workTime(d.accepted_min)}
                            {d.accepted_by && <span className="text-fg-faint"> · {ACCEPTED_BY[d.accepted_by]}</span>}
                          </>
                        ) : (
                          <span className="text-fg-faint">нет</span>
                        )}
                      </td>
                      <td className="px-2.5 py-1.5">
                        <ShiftBar size="sm" segments={d.bar.segments} accepted={d.bar.accepted} />
                      </td>
                      <td className="py-1.5 pl-2 text-right">
                        <Link to={shiftHref(p.user.id, d.date)} className="whitespace-nowrap text-accent-text hover:underline">
                          смена →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

function PersonRow({ p, days, open, onToggle }: { p: AcceptPerson; days: string[]; open: boolean; onToggle: () => void }) {
  const navigate = useNavigate();
  const t = p.totals;
  const byDate = new Map(p.days.map((d) => [d.date, d]));
  return (
    <>
      <tr className="cursor-pointer border-t border-border hover:bg-bg-hover" onClick={onToggle} data-annot={`сотрудник «${p.user.name}»`}>
        <td className="px-4 py-2">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 text-fg-muted" aria-hidden>
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
            <div className="min-w-0">
              <button type="button" aria-expanded={open} className="text-left font-medium text-fg" onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}>
                {p.user.name}
              </button>
              <div className="text-[11.5px] text-fg-faint">
                {p.location ?? "—"} · {RULE_LABEL[p.rule]}
              </div>
            </div>
          </div>
        </td>
        <td className={td}>
          {t.shifts}
          {t.hr_days > 0 && <div className="text-[11.5px] text-err">HR-дней {t.hr_days}</div>}
        </td>
        <td className={td}>{workTime(t.paid_min)}</td>
        <td className={td}>
          {workTime(t.auto_min)}
          {t.auto_pending_min > 0 && <div className="text-[11.5px] text-fg-faint">не в CRM {workTime(t.auto_pending_min)}</div>}
        </td>
        <td className={td}>
          {t.manual_count ? (
            <>
              <span className="text-warn">{workTime(t.manual_min)}</span>
              <div className="text-[11.5px] text-fg-faint">{t.manual_count} окон</div>
            </>
          ) : (
            <span className="text-fg-faint">—</span>
          )}
        </td>
        <td className={td}>
          {workTime(t.accepted_min)}
          {t.robot_days > 0 && <div className="text-[11.5px] text-fg-faint">Роботом {t.robot_days} дн.</div>}
        </td>
        <td className="px-2.5 py-2">
          <DayStrip days={days} byDate={byDate} onPick={(d) => navigate(shiftHref(p.user.id, d))} />
        </td>
      </tr>
      {open && <Shifts p={p} />}
    </>
  );
}

export function AcceptView({
  from,
  to,
  location,
  user,
  rule,
  onFilter,
}: {
  from: string;
  to: string;
  location: string;
  user: string;
  rule: Rule | "";
  onFilter: (next: { location?: string; user?: string; rule?: Rule | "" }) => void;
}) {
  const days = daysOf(from, to);
  // Все салоны дольше 14 дней сервер не считает — не спрашиваем, просим выбрать салон.
  const tooLong = !location && days.length > ALL_SALONS_MAX_DAYS;
  const q = useQuery({
    queryKey: ["activity-accept", from, to, location, rule],
    queryFn: () => loadAcceptPeriod({ from, to, location, rule }),
    placeholderData: keepPreviousData,
    enabled: !tooLong,
    retry: retryServerErrors,
  });
  // Салоны для выбора, когда период ещё не загружен: один день, только «без правила» — дёшево.
  const salons = useQuery({
    queryKey: ["activity-accept-locations"],
    queryFn: () => loadAcceptPeriod({ from: to, to, rule: "none" }).then((r) => r.locations),
    enabled: tooLong,
    staleTime: Infinity,
  });
  const [verdict, setVerdict] = useState<Verdict | "">("");
  const [open, setOpen] = useState<number | null>(null);
  const locations = q.data?.locations ?? salons.data ?? [];

  const all = q.data?.people ?? [];
  const byUser = user ? all.filter((p) => String(p.user.id) === user) : all;
  const people = verdict ? byUser.filter((p) => p.days.some((d) => d.verdict === verdict)) : byUser;
  const t = sum(people);
  const summary = Object.fromEntries(VERDICTS.map((v) => [v, 0])) as Record<Verdict, number>;
  for (const p of byUser) for (const d of p.days) summary[d.verdict] += 1;
  const userOptions = [
    { value: "", label: "все" },
    ...all.map((p) => ({ value: String(p.user.id), label: p.user.name })),
    ...(user && !all.some((p) => String(p.user.id) === user) ? [{ value: user, label: `#${user}` }] : []),
  ];

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted" data-annot="фильтры автоакцепта">
        <label className="flex items-center gap-2">
          Салон
          <select
            value={location}
            onChange={(e) => onFilter({ location: e.target.value, user: "" })}
            className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg"
          >
            <option value="">все салоны</option>
            {locations.map((l) => (
              <option key={l.id} value={String(l.id)}>
                {l.name}
              </option>
            ))}
            {location && !locations.some((l) => String(l.id) === location) && <option value={location}>#{location}</option>}
          </select>
        </label>
        <Combo label="Сотрудник" value={user} options={userOptions} onChange={(u) => onFilter({ user: u })} placeholder="имя сотрудника" width={240} />
        <div className="flex items-center gap-1" data-annot="правило">
          {(["", ...RULES] as (Rule | "")[]).map((r) => (
            <button
              key={r || "all"}
              type="button"
              onClick={() => onFilter({ rule: r })}
              aria-pressed={rule === r}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${rule === r ? "border-fg bg-bg text-fg" : "border-border text-fg-muted hover:bg-bg-hover"}`}
            >
              {r ? RULE_LABEL[r] : "все правила"}
            </button>
          ))}
        </div>
      </div>

      {tooLong ? (
        <div className="text-[12.5px] text-warn">
          Все салоны — не длиннее {ALL_SALONS_MAX_DAYS} дней: выберите салон или период короче.
        </div>
      ) : q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Читаю статусы, акцепт и действия из CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить автоакцепт: {String(q.error.message)}</div>
      ) : (
        <div className={`flex flex-col gap-4 ${q.isPlaceholderData ? "opacity-60" : ""}`}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Tile label="Смен" value={t.shifts} hint={`чел.-дней со статусами или акцептом${t.hr_days ? `; целиком HR — ${t.hr_days}` : ""}`} />
            <Tile label="По статусам" value={workTime(t.paid_min)} hint="оплачиваемые статусы: Работаю, Задача" />
            <Tile label="Автоакцепт" value={workTime(t.auto_min)} hint={`ещё не в CRM ${workTime(t.auto_pending_min)}`} />
            <Tile label="Требует решения" value={workTime(t.manual_min)} hint={`${t.manual_count} окон`} />
            <Tile label="Принято в CRM" value={workTime(t.accepted_min)} hint={`Роботом ${t.robot_days} дн.`} />
          </div>

          <div className="flex flex-wrap items-center gap-1.5 text-[12px]" data-annot="сводка вердиктов">
            {VERDICTS.filter((v) => summary[v] > 0).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVerdict(verdict === v ? "" : v)}
                aria-pressed={verdict === v}
                title={`${VERDICT[v].hint}. Клик — только сотрудники с такими днями`}
                className={`rounded-[var(--radius-ui)] px-2 py-0.5 ${VERDICT[v].tone} ${verdict === v ? "outline outline-1 outline-fg" : verdict ? "opacity-50" : ""}`}
              >
                {VERDICT[v].label} {summary[v]}
              </button>
            ))}
            {verdict && (
              <button type="button" onClick={() => setVerdict("")} className="px-1 text-fg-muted hover:text-fg">
                сбросить
              </button>
            )}
          </div>

          <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="сотрудники автоакцепта">
            {people.length === 0 ? (
              <div className="p-4 text-[12.5px] text-fg-muted">За этот период смен нет.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="text-left text-[11.5px] text-fg-faint">
                    <tr>
                      <th className="px-4 py-2 font-medium">Сотрудник</th>
                      <th className={th} title="чел.-дней со статусами или акцептом; HR-дней — день целиком решает HR (нет статусов, нет «Ушел», «Ушел» от CRM)">Смены</th>
                      <th className={th} title="время в оплачиваемых статусах: Работаю, Задача">По статусам</th>
                      <th className={th} title="что примет правило группы без HR; «не в CRM» — дни без акцепта: это поставил бы Робот">Автоакцепт</th>
                      <th className={th} title="окна простоя дольше 20 минут и дни целиком HR — минуты и число окон">Требует решения</th>
                      <th className={th} title="уже стоит в CRM (sUser_time_confirm), кем поставлено">Принято в CRM</th>
                      <th className="px-2.5 py-2 font-medium" title="квадрат на день: зелёный — пройдёт или принято, оранжевый — есть окна или расходится, красный — день целиком HR, серый — без правила, рамка — смены нет. Клик — смена">
                        Дни
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((p) => (
                      <PersonRow key={p.user.id} p={p} days={days} open={open === p.user.id} onToggle={() => setOpen(open === p.user.id ? null : p.user.id)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <div className="text-[12px] text-fg-faint">
            Правила: «Полный акцепт» — группа CRM 41, принимается всё время в статусах; «Стилисты без группы» — разрыв
            между действиями в CRM дольше 20 минут становится окном «требует решения»; «Без правила» — мастера,
            комплектовщики и руководители вне группы, всё решает HR. В CRM экран не пишет: акцепт ставят в Резерве.
            Клик по строке — смены по дням, по квадрату дня — смена с периодами и действиями.
          </div>
        </div>
      )}
    </>
  );
}
